import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from './ui/icon'
import {
    mdiAccountEdit,
    mdiFunctionVariant,
    mdiRobot,
    mdiRobotOutline,
    mdiShieldCheck,
    mdiMapMarkerAlertOutline,
    mdiMapOutline,
    mdiMapMarkerOutline,
    mdiUpload,
    mdiSigma,
    mdiCheck,
    mdiCheckCircleOutline,
    mdiCheckDecagramOutline,
    mdiOpenInNew
} from '@mdi/js'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Button } from './ui/button'
import { useAsset, useDocument } from '../hooks/useQueries'
import { DerivationProvenance } from './derivations/DerivationProvenance'
import { getLocalizedName } from '../lib/localization'
import { buildPageHighlightSearch, type HighlightBox, type PageHighlightOrigin } from '../lib/documentPageHighlight'
import { SECTION_ICONS } from '../lib/section-icons'

interface PropertySourceBadgeProps {
    org: string
    sourceCode?: string
    sourceDetails?: Record<string, any> | null
    createdByName?: string | null
    updatedByName?: string | null
    /** When the value was last written; shown with the time on a manual value. */
    updatedAt?: string | null
    approvedByName?: string | null
    approvedAt?: string | null
    flags?: string[]
    onConfirm?: () => void
    confirmPending?: boolean
    /**
     * True for a non-pending value that was merged into the registry by the
     * Fiabilisation module's server-side classify() (spec
     * 2026-06-29-buildtory-data-reliability.md §5.1) rather than by a human
     * clicking accept/confirm — i.e. present && !approvedByUid on a
     * non-`to_approve` row. Renders a small "Auto" chip so an auto-merged
     * value stays visually distinguishable from a human-confirmed one, per
     * spec §5's AUTO state and acceptance criterion #2.
     */
    isAutoMerged?: boolean
    /** Visual size of the trigger. 'md' is more readable (Data tab). */
    size?: 'sm' | 'md'
    /**
     * The asset/property or contract/clause this badge is describing —
     * threaded into the "view in document" deep link's `from` param so the
     * viewer's highlight box can navigate back here. Omitted whenever the
     * caller doesn't cheaply know its own entity; the link then behaves
     * exactly as before.
     */
    origin?: PageHighlightOrigin
}

const SOURCE_CONFIG: Record<string, { icon: string; labelKey: string }> = {
    manual: { icon: mdiAccountEdit, labelKey: 'detail.source_manual' },
    document_extraction: { icon: SECTION_ICONS.documents, labelKey: 'detail.source_document' },
    derivation: { icon: mdiFunctionVariant, labelKey: 'detail.source_computed' },
    // Values the platform computes itself under the `computation` source: the
    // cost of ownership of an equipment, the kpi_* of an agency or a site.
    computation: { icon: mdiFunctionVariant, labelKey: 'detail.source_computed' },
    propagation: { icon: mdiRobotOutline, labelKey: 'detail.source_inherited' },
    import: { icon: mdiUpload, labelKey: 'detail.source_import' },
    aggregation: { icon: mdiSigma, labelKey: 'detail.source_aggregated' },
    compliance: { icon: mdiShieldCheck, labelKey: 'detail.source_compliance' },
    // Geo-enrichment providers (parcel Cadastre tab): BAN geocoding, IGN
    // cadastre, Géorisques risk zoning. Values carry source_details.source set to
    // one of these codes.
    georisques: { icon: mdiMapMarkerAlertOutline, labelKey: 'detail.source_georisques' },
    ign_cadastre: { icon: mdiMapOutline, labelKey: 'detail.source_ign_cadastre' },
    ban: { icon: mdiMapMarkerOutline, labelKey: 'detail.source_ban' },
    // A value an agent run wrote (spec 2026-10-21-14 §10): the server forces
    // source_details.source to `agent`, with the run's mission, model, harness.
    agent: { icon: mdiRobot, labelKey: 'detail.source_agent' },
}

/** The confidence an agent stated, as a percentage. Display only. */
function agentConfidencePercent(value: unknown): number | null {
    const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
    if (!Number.isFinite(n) || n < 0) return null
    return Math.round(n <= 1 ? n * 100 : n)
}

export function PropertySourceBadge({
    org,
    sourceCode,
    sourceDetails,
    createdByName,
    updatedByName,
    updatedAt,
    approvedByName,
    approvedAt,
    flags,
    onConfirm,
    confirmPending,
    isAutoMerged,
    size = 'sm',
    origin,
}: PropertySourceBadgeProps) {
    const { t, i18n } = useTranslation('assets')
    const md = size === 'md'

    const formattedApprovedAt = approvedAt
        ? new Date(approvedAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' })
        : null

    const formattedUpdatedAt = updatedAt
        ? new Date(updatedAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
        : null

    // Resolve source type. Normalize the legacy `ai_extraction` alias used by
    // the asset-extraction job into the canonical `document_extraction` so both
    // funnel through the same UI path (link to source doc + uploader line).
    const rawSource = sourceCode || sourceDetails?.source || 'manual'
    const resolvedSource = rawSource === 'ai_extraction' ? 'document_extraction' : rawSource
    const config = SOURCE_CONFIG[resolvedSource] || SOURCE_CONFIG.manual

    // For manual source, show the user name as label
    const userName = updatedByName || createdByName
    const label = resolvedSource === 'manual' && userName
        ? userName
        : t(config.labelKey)

    // Propagation source asset
    const sourceAssetUid = sourceDetails?.source_asset_uid || ''
    const isPropagation = resolvedSource === 'propagation'
    const isAutoPropagated = flags?.includes('auto_propagated')

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex items-center ${md ? 'gap-1.5 text-sm max-w-[220px]' : 'gap-1 text-xs max-w-[160px]'} text-muted-foreground hover:text-foreground cursor-pointer transition-colors truncate`}
                    data-testid="property-source-badge"
                >
                    <AppIcon path={config.icon} size={md ? 'sm' : 'xs'} className="flex-shrink-0" />
                    <span className="truncate">{label}</span>
                    {isAutoMerged && (
                        <span
                            className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success"
                            data-testid="property-auto-merged-badge"
                        >
                            <AppIcon path={mdiCheckDecagramOutline} size="xs" />
                            {t('detail.auto_validated_badge')}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                        {t(config.labelKey)}
                    </p>

                    {/* Manual source: show user name */}
                    {resolvedSource === 'manual' && userName && (
                        <p className="text-sm" data-testid="property-source-modified-by">
                            {t('detail.source_modified_by', { name: userName })}
                        </p>
                    )}
                    {resolvedSource === 'manual' && formattedUpdatedAt && (
                        <p className="text-xs text-muted-foreground" data-testid="property-source-modified-on">
                            {t('detail.source_modified_on', { date: formattedUpdatedAt })}
                        </p>
                    )}

                    {/* Document extraction: link to source document with name */}
                    {resolvedSource === 'document_extraction' && sourceDetails?.document_uid && (
                        <DocumentSourceLink org={org} docUid={sourceDetails.document_uid} />
                    )}

                    {/* Any source with a page reference: verbatim quote (when present)
                        + "view in document" deep link (spec 2026-07-17-14 §2) — not
                        limited to document_extraction, e.g. an imported value written
                        with source_details: {source:"import", document_uid, page}
                        (spec 11's write path) gets the same deep link. Degradation
                        ladder: resolved -> boxed highlight, page_only/other -> page
                        without boxes. No page -> nothing renders here; for
                        document_extraction specifically, the plain DocumentSourceLink
                        above is all there is (unchanged). */}
                    {sourceDetails?.document_uid && typeof sourceDetails?.page === 'number' && (
                        <DocumentPageSourceDetails org={org} sourceDetails={sourceDetails} origin={origin} />
                    )}

                    {/* Document extraction: original uploader attribution */}
                    {resolvedSource === 'document_extraction' && createdByName && (
                        <p className="text-xs text-muted-foreground" data-testid="property-uploaded-by">
                            {t('detail.uploaded_by', { name: createdByName })}
                        </p>
                    )}

                    {/* Agent: the run's mission, model and harness, and the
                        confidence it stated (display only, never a gate). */}
                    {resolvedSource === 'agent' && (
                        <p className="text-sm" data-testid="property-source-agent-run">
                            {[sourceDetails?.mission, sourceDetails?.model, sourceDetails?.harness].filter(Boolean).join(' · ')}
                        </p>
                    )}
                    {resolvedSource === 'agent' && agentConfidencePercent(sourceDetails?.confidence) !== null && (
                        <p className="text-xs text-muted-foreground" data-testid="property-source-agent-confidence">
                            {t('detail.source_agent_confidence', { value: agentConfidencePercent(sourceDetails?.confidence) })}
                        </p>
                    )}

                    {/* Derivation: the rule, its matched row and the inputs it
                        read (spec 2026-10-06-15); the code alone on older rows. */}
                    {resolvedSource === 'derivation' && sourceDetails?.derivation_code && (
                        <DerivationProvenance org={org} sourceDetails={sourceDetails} />
                    )}

                    {/* Computation of a published KPI: its basis (spec 2026-10-04-01) */}
                    {resolvedSource === 'computation' && sourceDetails?.kpi_code && (
                        <p className="text-sm text-muted-foreground" data-testid="computation-kpi-basis">
                            {t('detail.source_kpi_basis', {
                                code: sourceDetails.kpi_code,
                                window: sourceDetails.window,
                                n: sourceDetails.n,
                                date: sourceDetails.snapshot_date,
                            })}
                        </p>
                    )}

                    {/* Propagation: link to source asset */}
                    {isPropagation && sourceAssetUid && (
                        <PropagationSourceLink org={org} sourceAssetUid={sourceAssetUid} />
                    )}

                    {/* Compliance: link to the dominant rule when available */}
                    {resolvedSource === 'compliance' && sourceDetails?.compliance_rule_uid && (
                        <Link
                            to="/$org/compliance-rules/$ruleUid"
                            params={{ org, ruleUid: sourceDetails.compliance_rule_uid }}
                            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                            data-testid="compliance-source-rule-link"
                        >
                            <span className="truncate">
                                {getLocalizedName(sourceDetails.compliance_rule_name, i18n.language) ||
                                    sourceDetails.compliance_rule_code ||
                                    t('detail.source_compliance_view_rule')}
                            </span>
                            <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0" />
                        </Link>
                    )}

                    {/* Compliance: summary of how many rules failed */}
                    {resolvedSource === 'compliance' && sourceDetails?.compliance_rule_count ? (
                        <p className="text-xs text-muted-foreground">
                            {t('detail.source_compliance_rule_summary', {
                                failing: sourceDetails.compliance_failing_rule_count ?? 0,
                                total: sourceDetails.compliance_rule_count,
                            })}
                        </p>
                    ) : null}

                    {/* Validated-by attribution after approval */}
                    {approvedByName && formattedApprovedAt && (
                        <div
                            className="flex items-center gap-1 pt-1 border-t text-xs text-muted-foreground"
                            data-testid="property-validated-by"
                        >
                            <AppIcon path={mdiCheckCircleOutline} size="xs" />
                            {t('detail.validated_by_on', {
                                name: approvedByName,
                                date: formattedApprovedAt,
                            })}
                        </div>
                    )}

                    {/* Auto-merged explanation — classify() resolved this
                        candidate without a human ever clicking accept. */}
                    {isAutoMerged && (
                        <div
                            className="flex items-start gap-1 pt-1 border-t text-xs text-success"
                            data-testid="property-auto-merged-explanation"
                        >
                            <AppIcon path={mdiCheckDecagramOutline} size="xs" className="mt-0.5 flex-shrink-0" />
                            <span>{t('detail.auto_validated_explanation')}</span>
                        </div>
                    )}

                    {/* Confirm button for auto-propagated values */}
                    {isPropagation && isAutoPropagated && onConfirm && (
                        <div className="pt-1 border-t">
                            <Button
                                onClick={onConfirm}
                                disabled={confirmPending}
                                variant="outline"
                                size="sm"
                                className="w-full text-xs"
                                data-testid="confirm-propagated-property-btn"
                            >
                                <AppIcon path={mdiCheck} size="xs" className="mr-1" />
                                {t('detail.confirm_value')}
                            </Button>
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}

function PropagationSourceLink({ org, sourceAssetUid }: { org: string; sourceAssetUid: string }) {
    const { t } = useTranslation('assets')
    const { data: sourceAsset } = useAsset(org, sourceAssetUid)

    return (
        <Link
            to="/$org/assets/$assetUid"
            params={{ org, assetUid: sourceAssetUid }}
            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            data-testid="propagation-source-link"
        >
            {sourceAsset?.name || t('detail.view_source_equipment')}
            <AppIcon path={mdiOpenInNew} size="xs" />
        </Link>
    )
}

function DocumentPageSourceDetails({
    org,
    sourceDetails,
    origin,
}: {
    org: string
    sourceDetails: Record<string, any>
    origin?: PageHighlightOrigin
}) {
    const { t } = useTranslation('assets')
    const docUid: string = sourceDetails.document_uid
    const page: number = sourceDetails.page
    const quote: string | undefined = sourceDetails.quote
    const geometryStatus: string | undefined = sourceDetails.geometry_status
    const boxes: HighlightBox[] | undefined =
        geometryStatus === 'resolved' && Array.isArray(sourceDetails.bounding_boxes) && sourceDetails.bounding_boxes.length > 0
            ? sourceDetails.bounding_boxes
            : undefined

    return (
        <div className="space-y-1.5">
            {quote && (
                <blockquote
                    className="border-l-2 border-primary/40 pl-2 text-xs italic text-muted-foreground"
                    data-testid="property-source-quote"
                >
                    &ldquo;{quote}&rdquo;
                </blockquote>
            )}
            <Link
                to="/$org/documents/$docUid"
                params={{ org, docUid }}
                search={buildPageHighlightSearch(page, boxes, origin) as never}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                data-testid="view-source-in-document"
            >
                <span className="truncate">{t('detail.view_in_document', { page })}</span>
                <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0" />
            </Link>
        </div>
    )
}

function DocumentSourceLink({ org, docUid }: { org: string; docUid: string }) {
    const { t } = useTranslation('assets')
    const { data: document } = useDocument(org, docUid)
    const label = document?.name
        ? t('detail.source_extracted_from_named', { name: document.name })
        : t('detail.source_extracted_from')

    return (
        <Link
            to="/$org/documents/$docUid"
            params={{ org, docUid }}
            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
            data-testid="property-source-document-link"
        >
            <span className="truncate">{label}</span>
            <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0" />
        </Link>
    )
}
